import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_THEME_ID, type ThemeDef } from '@/constants/themes';

type SidebarTheme = 'dark' | 'light';
export type ThemeMode = 'light' | 'dark';
/** A `{ light, dark }` theme pair — used for both the tenant app theme and the marketing theme. */
export type CustomTheme = { light: ThemeDef; dark: ThemeDef };

interface UIState {
  mobileNavOpen: boolean;
  sidebarTheme: SidebarTheme;
  /**
   * Whether the user has explicitly chosen a sidebar theme. When false, the
   * sidebar mirrors `themeMode`. Set to true the moment the user toggles the
   * sidebar theme directly (see setSidebarTheme / toggleSidebarTheme below)
   * so their override sticks across global-theme flips.
   */
  sidebarThemeOverridden: boolean;
  /** Global app theme. Independent of sidebarTheme so users can mix them. */
  themeMode: ThemeMode;
  /**
   * Selected theme preset id (see src/constants/themes.ts). The sentinel
   * 'default' keeps the legacy terracotta brand + themeMode toggle; any other
   * id drives the whole app's palette and semantic tokens from that preset.
   */
  themeId: string;
  /**
   * The active tenant's app theme (from `builder.theme`, delivered via
   * /auth/me). When non-null it overrides the preset app-wide for the logged-in
   * tenant. Cached in localStorage so it applies instantly on reload; refreshed
   * from the backend on load and cleared on logout / for tenants without a theme.
   */
  customTheme: CustomTheme | null;
  /**
   * Platform-wide MARKETING/landing theme, authored by a super-admin (global
   * Settings.theme, served via /public/theme). Skins ONLY logged-out marketing
   * and login surfaces (the `--mk-*` CSS vars) — kept separate from the per-tenant
   * app theme above. In-memory only: SSR publishes it and the client re-fetches.
   */
  marketingTheme: CustomTheme | null;
  /** ⌘K command palette open state. */
  commandPaletteOpen: boolean;
}

const STORAGE_KEY = 'sidebar-theme';
const SIDEBAR_OVERRIDE_KEY = 'sidebar-theme-overridden';
const THEME_MODE_KEY = 'theme-mode';
// v2 key: the old 'theme-id' could hold a stale lab selection. Bumping the key
// ensures every browser falls back to the committed default theme.
const THEME_ID_KEY = 'app-theme-v2';

/** SSR-safe reader for the persisted sidebar theme. */
function readPersistedSidebarTheme(): SidebarTheme {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'dark' || stored === 'light' ? stored : 'light';
  } catch {
    return 'light';
  }
}

/** SSR-safe writer that swallows quota / privacy-mode errors. */
function persistSidebarTheme(theme: SidebarTheme): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* localStorage may be unavailable (Safari private mode, etc.) */
  }
}

/** SSR-safe reader for the persisted global theme. */
function readPersistedThemeMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(THEME_MODE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function persistThemeMode(mode: ThemeMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_MODE_KEY, mode);
  } catch { /* swallow */ }
}

/** SSR-safe reader for the persisted theme preset id. */
function readPersistedThemeId(): string {
  if (typeof window === 'undefined') return DEFAULT_THEME_ID;
  try {
    return window.localStorage.getItem(THEME_ID_KEY) || DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

const CUSTOM_THEME_KEY = 'app-custom-theme-v1';
function readPersistedCustomTheme(): CustomTheme | null {
  if (typeof window === 'undefined') return null;
  try { const s = window.localStorage.getItem(CUSTOM_THEME_KEY); return s ? (JSON.parse(s) as CustomTheme) : null; } catch { return null; }
}
function persistCustomTheme(t: CustomTheme | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (t) window.localStorage.setItem(CUSTOM_THEME_KEY, JSON.stringify(t));
    else window.localStorage.removeItem(CUSTOM_THEME_KEY);
  } catch { /* localStorage may be unavailable */ }
}
function persistThemeId(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(THEME_ID_KEY, id);
  } catch { /* swallow */ }
}

function readSidebarOverride(): boolean {
  if (typeof window === 'undefined') return false;
  try { return window.localStorage.getItem(SIDEBAR_OVERRIDE_KEY) === '1'; }
  catch { return false; }
}
function persistSidebarOverride(v: boolean): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(SIDEBAR_OVERRIDE_KEY, v ? '1' : '0'); } catch { /* swallow */ }
}

const initialState: UIState = {
  mobileNavOpen: false,
  sidebarTheme: readPersistedSidebarTheme(),
  sidebarThemeOverridden: readSidebarOverride(),
  themeMode: readPersistedThemeMode(),
  themeId: readPersistedThemeId(),
  customTheme: readPersistedCustomTheme(),
  marketingTheme: null,
  commandPaletteOpen: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openMobileNav: (state) => {
      state.mobileNavOpen = true;
    },
    closeMobileNav: (state) => {
      state.mobileNavOpen = false;
    },
    toggleMobileNav: (state) => {
      state.mobileNavOpen = !state.mobileNavOpen;
    },
    toggleSidebarTheme: (state) => {
      state.sidebarTheme = state.sidebarTheme === 'dark' ? 'light' : 'dark';
      persistSidebarTheme(state.sidebarTheme);
      // Direct toggle = explicit override; lock the sidebar theme so a later
      // global-theme flip doesn't undo what the user just chose.
      state.sidebarThemeOverridden = true;
      persistSidebarOverride(true);
    },
    setSidebarTheme: (state, action: PayloadAction<SidebarTheme>) => {
      state.sidebarTheme = action.payload;
      persistSidebarTheme(state.sidebarTheme);
      state.sidebarThemeOverridden = true;
      persistSidebarOverride(true);
    },
    /**
     * Used by the auto-sync hook: changes the sidebar theme to mirror the
     * global theme WITHOUT marking it as user-overridden. Lets the sidebar
     * keep following global until the user explicitly diverges.
     */
    syncSidebarToGlobal: (state, action: PayloadAction<SidebarTheme>) => {
      state.sidebarTheme = action.payload;
      persistSidebarTheme(state.sidebarTheme);
    },
    /** Drop the user's override and let the sidebar follow the global theme again. */
    clearSidebarThemeOverride: (state) => {
      state.sidebarThemeOverridden = false;
      persistSidebarOverride(false);
    },
    toggleThemeMode: (state) => {
      state.themeMode = state.themeMode === 'dark' ? 'light' : 'dark';
      persistThemeMode(state.themeMode);
    },
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = action.payload;
      persistThemeMode(state.themeMode);
    },
    /** Apply a theme preset app-wide (or 'default' to restore the brand). */
    setThemeId: (state, action: PayloadAction<string>) => {
      state.themeId = action.payload;
      persistThemeId(state.themeId);
    },
    /** Apply/clear the active tenant's app theme (from builder.theme). */
    setCustomTheme: (state, action: PayloadAction<CustomTheme | null>) => {
      state.customTheme = action.payload;
      persistCustomTheme(action.payload);
    },
    /** Apply/clear the platform-wide marketing/landing theme (global Settings.theme). */
    setMarketingTheme: (state, action: PayloadAction<CustomTheme | null>) => {
      state.marketingTheme = action.payload;
    },
    openCommandPalette:  (state) => { state.commandPaletteOpen = true; },
    closeCommandPalette: (state) => { state.commandPaletteOpen = false; },
    toggleCommandPalette: (state) => { state.commandPaletteOpen = !state.commandPaletteOpen; },
  },
});

export const {
  openMobileNav, closeMobileNav, toggleMobileNav,
  toggleSidebarTheme, setSidebarTheme,
  syncSidebarToGlobal, clearSidebarThemeOverride,
  toggleThemeMode, setThemeMode, setThemeId, setCustomTheme, setMarketingTheme,
  openCommandPalette, closeCommandPalette, toggleCommandPalette,
} = uiSlice.actions;
export default uiSlice.reducer;
